import { uiSurface } from "shared/ui/uiSurface"
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import dayjs, { Dayjs } from "dayjs"
import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router"
import {
  ReportCourier,
  ReportProvider,
  UniversalReport,
  useFetchReportMutation,
  useGetReportParametersQuery,
} from "../../apis/appApi"
import { pathConstants } from "../../pathConstants"
import { AccountingTab } from "./AccountingTab"
import { ConformityTab } from "./ConformityTab"
import { ImmobilityTab } from "./ImmobilityTab"
import { TravelTab } from "./TravelTab"

const tabs = [
  { label: "reports.tabs.accounting", path: pathConstants.reportsAccounting },
  { label: "reports.tabs.travel", path: pathConstants.reportsTravel },
  { label: "reports.tabs.conformity", path: pathConstants.reportsConformity },
  { label: "reports.tabs.immobility", path: pathConstants.reportsImmobility },
]

export const ReportsPage: FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()

  // Parameters
  const { data: parameters } = useGetReportParametersQuery()
  const maxDays = parameters?.maxDaysAllowedForReports ?? 31

  // Date range state
  const [fromDate, setFromDate] = useState<Dayjs>(dayjs().subtract(7, "day"))
  const [toDate, setToDate] = useState<Dayjs>(dayjs())

  // Report data
  const [fetchReport, { isLoading }] = useFetchReportMutation()
  const [report, setReport] = useState<UniversalReport | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Filters
  const [visibleCourierIds, setVisibleCourierIds] = useState<number[]>([])
  const [visibleProviderIds, setVisibleProviderIds] = useState<string[]>([])

  // Tab sync
  const activeIndex = tabs.findIndex((tab) =>
    location.pathname.startsWith(tab.path),
  )

  useEffect(() => {
    if (
      location.pathname === pathConstants.reports ||
      location.pathname === pathConstants.reports + "/"
    ) {
      navigate(pathConstants.reportsAccounting, { replace: true })
    }
  }, [location.pathname, navigate])

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    navigate(tabs[newValue].path)
  }

  // Reset filters when report changes
  useEffect(() => {
    if (report) {
      setVisibleCourierIds(report.couriers.map((c) => c.id))
      setVisibleProviderIds(report.providers.map((p) => p.id))
    }
  }, [report])

  // Fetch handler
  const handleFetch = useCallback(async () => {
    setError(null)
    const diffDays = toDate.diff(fromDate, "day")
    if (diffDays > maxDays) {
      setError(t("reports.errors.maxDaysExceeded", { max: maxDays }))
      return
    }
    if (diffDays < 0) {
      setError(t("reports.errors.invalidRange"))
      return
    }
    try {
      const result = await fetchReport({
        from: fromDate.format("YYYY-MM-DD"),
        to: toDate.format("YYYY-MM-DD"),
      }).unwrap()
      setReport(result)
    } catch (err: any) {
      setError(err?.data?.message || t("reports.errors.fetchFailed"))
    }
  }, [fromDate, toDate, maxDays, fetchReport, t])

  // Filtered entries
  const filteredEntries = useMemo(() => {
    if (!report) return []
    return report.entries.filter((entry) => {
      if (entry.courier && !visibleCourierIds.includes(entry.courier.id))
        return false
      const providerId = entry.order.providerId
      if (providerId && !visibleProviderIds.includes(providerId)) return false
      return true
    })
  }, [report, visibleCourierIds, visibleProviderIds])

  // Immobility tab has its own controls and data — detect if active
  const isImmobilityTab = location.pathname.startsWith(
    pathConstants.reportsImmobility,
  )

  const renderTabContent = () => {
    if (!report) return null
    const currentPath = location.pathname
    const props = {
      entries: filteredEntries,
      report,
      parameters: parameters!,
    }
    if (currentPath.startsWith(pathConstants.reportsTravel))
      return <TravelTab {...props} />
    if (currentPath.startsWith(pathConstants.reportsConformity))
      return <ConformityTab {...props} />
    return <AccountingTab {...props} />
  }

  return (
    <Box
      data-testid="reports-page"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          data-testid="reports-tabs"
          value={activeIndex === -1 ? 0 : activeIndex}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: { xs: 2, md: 3 } }}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.path}
              data-testid={`reports-tab-${tab.path}`}
              label={t(tab.label)}
              sx={{ textTransform: "none" }}
            />
          ))}
        </Tabs>
      </Box>

      {isImmobilityTab ? (
        /* Immobility tab manages its own controls and data */
        <Box
          data-testid="reports-tab-content"
          sx={{ flex: 1, overflow: "auto", px: { xs: 2, md: 3 }, py: 2, pb: { xs: 2, md: 3 } }}
        >
          <ImmobilityTab />
        </Box>
      ) : (
        <>
          {/* Shared controls bar for Accounting / Travel / Conformity */}
          <Box
            {...uiSurface("UI-DLV2-191")}
            sx={{
              px: { xs: 2, md: 3 },
              py: 2,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", md: "center" }}
              flexWrap="wrap"
            >
              <Box data-testid="reports-from-date">
                <DatePicker
                  label={t("reports.dateRange.from")}
                  value={fromDate}
                  onChange={(v) => v && setFromDate(v)}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: { width: { xs: "100%", md: 180 } },
                    },
                  }}
                />
              </Box>
              <Box data-testid="reports-to-date">
                <DatePicker
                  label={t("reports.dateRange.to")}
                  value={toDate}
                  onChange={(v) => v && setToDate(v)}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: { width: { xs: "100%", md: 180 } },
                    },
                  }}
                />
              </Box>
              <Button
                data-testid="reports-fetch-btn"
                variant="contained"
                onClick={handleFetch}
                disabled={isLoading}
                sx={{ width: { xs: "100%", md: "auto" } }}
              >
                {isLoading ? t("loading") : t("reports.actions.fetch")}
              </Button>
              <Typography variant="caption" color="text.secondary">
                {t("reports.dateRange.explain", { max: maxDays })}
              </Typography>
            </Stack>

            {error && (
              <Alert
                data-testid="reports-error-alert"
                severity="error"
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}

            {report && (
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                flexWrap="wrap"
              >
                <Autocomplete
                  data-testid="reports-couriers-autocomplete"
                  multiple
                  size="small"
                  options={report.couriers}
                  value={report.couriers.filter((c) =>
                    visibleCourierIds.includes(c.id),
                  )}
                  getOptionLabel={(o: ReportCourier) =>
                    `${o.lastName} ${o.firstName}`
                  }
                  onChange={(_, val) => setVisibleCourierIds(val.map((c) => c.id))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("reports.filters.couriers")}
                      sx={{ minWidth: { xs: 0, md: 250 } }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={`${option.lastName} ${option.firstName}`}
                        size="small"
                      />
                    ))
                  }
                />
                {report.providerSize > 1 && (
                  <Autocomplete
                    data-testid="reports-providers-autocomplete"
                    multiple
                    size="small"
                    options={report.providers}
                    value={report.providers.filter((p) =>
                      visibleProviderIds.includes(p.id),
                    )}
                    getOptionLabel={(o: ReportProvider) => o.name}
                    onChange={(_, val) =>
                      setVisibleProviderIds(val.map((p) => p.id))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("reports.filters.providers")}
                        sx={{ minWidth: { xs: 0, md: 250 } }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          label={option.name}
                          size="small"
                        />
                      ))
                    }
                  />
                )}
              </Stack>
            )}
          </Box>

          {/* Tab content */}
          <Box
            data-testid="reports-tab-content"
            sx={{ flex: 1, overflow: "auto", px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 } }}
          >
            {!report ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "text.secondary",
                }}
              >
                <Typography>{t("reports.empty.selectDateRange")}</Typography>
              </Box>
            ) : filteredEntries.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "text.secondary",
                }}
              >
                <Typography>{t("reports.empty.noData")}</Typography>
              </Box>
            ) : (
              renderTabContent()
            )}
          </Box>
        </>
      )}
    </Box>
  )
}
